import { FC } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Download } from '@factorialco/f0-react/icons/app'
import { BenefitsHealthcareEnrollmentPlanFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { WizardStepHeader } from 'modules/benefits/components/benefitModals/HealthcareEnrollmentWizard/components/WizardStepHeader'

interface Props {
  healthcarePlanData: BenefitsHealthcareEnrollmentPlanFragment
}

export const PlanOverviewContent: FC<Props> = ({ healthcarePlanData }) => {
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const { planData } = { planData: healthcarePlanData }
  // Get currency from employee's legal entity, fallback to EUR
  const currency = employee?.legalEntity?.currency ?? 'EUR'

  const formatCurrency = (cents: number) => {
    return MoneyWithCurrency({
      amount: cents,
      currency,
      locale,
    })
  }

  const formatWaitingPeriod = (months: number) => {
    if (months === 0)
      return i18n.t(
        'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period.no_waiting_period'
      )
    return i18n.tp(
      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period',
      { count: months }
    )
  }

  const providerAndPlanSummary = `${planData.providerName} ${planData.planName}`
  const planDocumentUrl = planData.planDocument?.url

  const dashedDividerStyle = {
    borderBottom: '1px dashed rgba(5, 35, 72, 0.14)',
    width: '100%',
  }

  const renderDivider = (style = dashedDividerStyle) => (
    <Box width="full" paddingY="s8">
      <Box width="full" style={style} />
    </Box>
  )

  const renderDetailRow = (
    label: string,
    value: string,
    labelWeight: 'semibold' | 'normal' = 'semibold'
  ) => (
    <Box
      width="full"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="flexEnd"
      style={{
        gap: '4px',
      }}
    >
      <Text.Normal weight={labelWeight} size="300">
        {label}
      </Text.Normal>
      <Text.Normal size="200">{value}</Text.Normal>
    </Box>
  )

  const renderCostRow = (
    label: string,
    value: string,
    labelWeight: 'semibold' | 'normal' = 'normal'
  ) => (
    <Box
      width="full"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="flexEnd"
      style={{
        gap: '4px',
      }}
    >
      <Text.Normal weight={labelWeight} size="200">
        {label}
      </Text.Normal>
      <Text.Normal size="200">{value}</Text.Normal>
    </Box>
  )

  const renderCostColumn = (
    title: string,
    totalCostCents: number,
    coveredCostCents: number,
    costType: 'employee' | 'dependent'
  ) => {
    const paidByCompanyLabel =
      costType === 'employee'
        ? i18n.t(
            'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_company.label'
          )
        : i18n.t(
            'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_company.label'
          )

    const paidByEmployeeLabel =
      costType === 'employee'
        ? i18n.t(
            'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_employee.label'
          )
        : i18n.t(
            'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_employee.label'
          )

    return (
      <Box flexDirection="column" width="full" gap="s8">
        {renderCostRow(title, formatCurrency(totalCostCents), 'semibold')}
        <Box width="full">
          {renderDivider()}
          {renderCostRow(paidByCompanyLabel, formatCurrency(coveredCostCents))}
          {renderDivider()}
          {renderCostRow(
            paidByEmployeeLabel,
            formatCurrency(Math.max(0, totalCostCents - coveredCostCents))
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box flexDirection="column" width="full" gap="s24" paddingBottom="s24">
      <WizardStepHeader
        title={i18n.t('benefits.healthcare.enrollment_wizard.plan_overview_step.title')}
        description={providerAndPlanSummary}
        action={
          planDocumentUrl ? (
            <F0Button
              icon={Download}
              label={i18n.t(
                'benefits.healthcare.enrollment_wizard.plan_overview_step.download_plan_coverage'
              )}
              variant="outline"
              size="md"
              href={planDocumentUrl}
              target="_blank"
              rel="noopener noreferrer"
            />
          ) : null
        }
      />

      <Box flexDirection="column" width="full" gap="s16">
        <Box width="full">
          {renderDetailRow(
            `${i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.renewal_date.label'
            )}:`,
            convertDate(planData.renewalDate, 'backend', 'ui')
          )}
          {renderDivider()}
          {renderDetailRow(
            `${i18n.t(
              'benefits.healthcare.enrollment_wizard.plan_overview_step.enrollment_conditions.waiting_period'
            )}:`,
            formatWaitingPeriod(planData.waitingPeriodMonths),
            'normal'
          )}
          {renderDivider()}
          {renderDetailRow(
            `${i18n.t(
              'benefits.healthcare.enrollment_wizard.plan_overview_step.enrollment_conditions.pre_existing_conditions.label'
            )}:`,
            planData.allowPreExistingConditions
              ? i18n.t(
                  'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.allowed'
                )
              : i18n.t(
                  'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.not_allowed'
                ),
            'normal'
          )}
        </Box>
        {renderDivider({ borderBottom: '1px solid rgba(5, 38, 87, 0.06)', width: '100%' })}

        <Box flexDirection="column" width="full" gap="s8">
          <Text.Normal weight="semibold" size="300">
            {i18n.t('benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.label')}
          </Text.Normal>
          <Box flexDirection="row" width="full" alignItems="flexStart" gap="s32">
            {renderCostColumn(
              i18n.t(
                'benefits.healthcare.enrollment_wizard.plan_overview_step.total_monthly_cost.employee.label'
              ),
              planData.employeeCostCents || 0,
              planData.coveredEmployeeCostCents || 0,
              'employee'
            )}

            {planData.dependentCostCents != null &&
              renderCostColumn(
                i18n.t(
                  'benefits.healthcare.enrollment_wizard.plan_overview_step.total_monthly_cost.dependent.label'
                ),
                planData.dependentCostCents,
                planData.coveredDependentCostCents || 0,
                'dependent'
              )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PlanOverviewContent
